import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '../components/ui/Table';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from '../layouts/MainLayout';
import { apiService } from '../services/api';
import { toast } from 'react-toastify';

interface Contact {
  id: number;
  name: string;
  phone_number: string;
  email: string;
  company: string;
  agent_name: string | null;
  created_at: string;
  updated_at: string;
}

interface ContactsResponse {
  success: boolean;
  data: {
    items: Contact[];
    pagination: {
      current_page: number;
      per_page: number;
      total: number;
      last_page: number;
      from: number;
      to: number;
    };
  };
  message?: string;
}

interface PaginationInfo {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
  from: number;
  to: number;
}

const AllContacts: React.FC = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    phone_number: '',
    email: '',
    company: ''
  });
  const { token, hasPermission } = useAuth();
  const navigate = useNavigate();

  const fetchContacts = async (page = 1, searchTerm = '') => {
    setLoading(true);
    try {
      const params: Record<string, any> = {
        page: page.toString(),
        per_page: '15',
      };
      
      if (searchTerm) {
        params.search = searchTerm;
      }

      const response = await apiService.get<ContactsResponse>('contacts', params);
      
      if (response && response.success && response.data && Array.isArray((response.data as any).items)) {
        setContacts((response.data as any).items);
        setPagination((response.data as any).pagination || null);
      } else {
        console.warn('Unexpected API response structure:', response);
        setContacts([]);
        setPagination(null);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setContacts([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchContacts(currentPage, search);
    }
  }, [token, currentPage]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchContacts(1, search);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleEdit = (contact: Contact) => {
    setEditingContact(contact);
    setEditForm({
      name: contact.name,
      phone_number: contact.phone_number,
      email: contact.email || '',
      company: contact.company || ''
    });
  };

  const handleSaveEdit = async () => {
    if (!editingContact) return;

    if (!hasPermission('all-contacts', 'edit')) {
      toast.error('You do not have permission to edit contacts.');
      return;
    }

    try {
      const response = await apiService.put(`contacts/${editingContact.id}`, editForm);
      
      if (response.success) {
        toast.success('Contact updated successfully');
        setEditingContact(null);
        fetchContacts(currentPage, search);
      } else {
        toast.error(response.message || 'Failed to update contact');
      }
    } catch (error) {
      console.error('Error updating contact:', error);
      toast.error('Error updating contact');
    }
  };

  const handleCancelEdit = () => {
    setEditingContact(null);
    setEditForm({
      name: '',
      phone_number: '',
      email: '',
      company: ''
    });
  };

  const handleDelete = async (contactId: number) => {
    if (!hasPermission('all-contacts', 'delete')) {
      toast.error('You do not have permission to delete contacts.');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this contact?')) {
      return;
    }

    try {
      const response = await apiService.delete(`contacts/${contactId}`);
      
      if (response.success) {
        toast.success('Contact deleted successfully');
        fetchContacts(currentPage, search);
      } else {
        toast.error(response.message || 'Failed to delete contact');
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Error deleting contact');
    }
  };

  const handleExport = async () => {
    if (!hasPermission('all-contacts', 'export')) {
      toast.error('You do not have permission to export contacts.');
      return;
    }
    
    try {
      await apiService.downloadFile('contacts/export', 'contacts_export.csv');
      toast.success('Contacts exported successfully');
    } catch (error) {
      console.error('Error exporting contacts:', error);
      toast.error('Error exporting contacts');
    }
  };

  const handlePrint = async () => {
    if (!hasPermission('all-contacts', 'print')) {
      toast.error('You do not have permission to print contacts.');
      return;
    }
    
    try {
      const response = await apiService.get('contacts/print');
      if (response.success) {
        toast.success('Contacts printed successfully');
      } else {
        toast.error('Failed to print contacts');
      }
    } catch (error) {
      console.error('Error printing contacts:', error);
      toast.error('Error printing contacts');
    }
  };

  return (
    <MainLayout>
      <div className="p-6">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            All Contacts
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage and view all contacts in the system
          </p>
        </div>

        {/* Search and Actions */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <form onSubmit={handleSearch} className="flex gap-2 flex-1 max-w-md">
              <Input
                type="text"
                placeholder="Search contacts..."
                value={search}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
                className="flex-1"
              />
              <Button type="submit" disabled={loading}>
                Search
              </Button>
            </form>
            <div className="flex gap-2">
              {hasPermission('all-contacts', 'print') && (
                <Button onClick={handlePrint} disabled={loading}>
                  Print
                </Button>
              )}
              {hasPermission('all-contacts', 'export') && (
                <Button onClick={handleExport} disabled={loading}>
                  Export CSV
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Contacts Table */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Phone Number</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Agent</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : contacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8">
                    No contacts found
                  </TableCell>
                </TableRow>
              ) : (
                contacts.map((contact) => (
                  <TableRow key={contact.id}>
                    <TableCell>{contact.id}</TableCell>
                    <TableCell>
                      {editingContact?.id === contact.id ? (
                        <Input
                          value={editForm.name}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                            setEditForm({ ...editForm, name: e.target.value })
                          }
                          className="w-full"
                        />
                      ) : (
                        contact.name
                      )}
                    </TableCell>
                    <TableCell>
                      {editingContact?.id === contact.id ? (
                        <Input
                          value={editForm.phone_number}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                            setEditForm({ ...editForm, phone_number: e.target.value })
                          }
                          className="w-full"
                        />
                      ) : (
                        contact.phone_number
                      )}
                    </TableCell>
                    <TableCell>
                      {editingContact?.id === contact.id ? (
                        <Input
                          value={editForm.email}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                            setEditForm({ ...editForm, email: e.target.value })
                          }
                          className="w-full"
                        />
                      ) : (
                        contact.email
                      )}
                    </TableCell>
                    <TableCell>
                      {editingContact?.id === contact.id ? (
                        <Input
                          value={editForm.company}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                            setEditForm({ ...editForm, company: e.target.value })
                          }
                          className="w-full"
                        />
                      ) : (
                        contact.company
                      )}
                    </TableCell>
                    <TableCell>{contact.agent_name || 'N/A'}</TableCell>
                    <TableCell>{new Date(contact.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>{new Date(contact.updated_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {editingContact?.id === contact.id ? (
                        <div className="flex space-x-2">
                          <Button
                            onClick={handleSaveEdit}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                          >
                            Save
                          </Button>
                          <Button
                            onClick={handleCancelEdit}
                            size="sm"
                            variant="outline"
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <div className="flex space-x-2">
                          {hasPermission('all-contacts', 'edit') && (
                            <Button
                              onClick={() => handleEdit(contact)}
                              size="sm"
                              variant="outline"
                            >
                              Edit
                            </Button>
                          )}
                          {hasPermission('all-contacts', 'delete') && (
                            <Button
                              onClick={() => handleDelete(contact.id)}
                              size="sm"
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Delete
                            </Button>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {pagination && pagination.last_page > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              Showing {pagination.from} to {pagination.to} of {pagination.total} results
            </div>
            <div className="flex space-x-2">
              <Button
                onClick={() => handlePageChange(pagination.current_page - 1)}
                disabled={pagination.current_page === 1}
                size="sm"
              >
                Previous
              </Button>
              <span className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
                Page {pagination.current_page} of {pagination.last_page}
              </span>
              <Button
                onClick={() => handlePageChange(pagination.current_page + 1)}
                disabled={pagination.current_page === pagination.last_page}
                size="sm"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default AllContacts; 